import React, {useState, useEffect} from 'react'
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import { TextField } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import Rating from '@mui/material/Rating';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';

export default function FormInputs(
    {
        precall = false,
        meetingId = null,
        pin = null,
        formInputs,
        resultWebhook = null,
        onHasAllRequired = () => {},
        onSipHeadersChange = () => {},
        onExtensionChange = () => {},
        onCallerNameChange = () => {},
        onWebhookParamsChange = () => {},
        onMeetingIdChange = () => {},
        onPINChange = () => {}
    }
) {
  const [inputs, setInputs] = useState([])
  const [formValues, setFormValues] = useState({})
  const [requiredInputs, setRequiredInputs] = useState([])
  const [disabledInputs, setDisabledInputs] = useState([])
  const [hasAllRequired, setHasAllRequired] = useState(false)
  const [inputSipHeaders, setInputSipHeaders] = useState({})
  const [webhookParams, setWebhookParams] = useState({})
  const [presetMeetingId, setPresetMeetingId] = useState(false)

  useEffect(() => {
    if (meetingId) setPresetMeetingId(true)
  }, [])

  useEffect(() => {
    if (!formInputs) return

    formInputs.forEach(input => {
        let valid = isValidInput(input)
        if (valid) {
            setInputs(prevArray => [...prevArray, input])
            if (input.required) setRequiredInputs(prevArray => [...prevArray, input])
            let values = formValues
            values[input.id] = input.defaultValue
            if (input.resultAction === 'meetingid') {
                values[input.id] = meetingId
                if (presetMeetingId) setDisabledInputs(prevArray => [...prevArray, input.id])
            }
            if (input.resultAction === 'pin') {
                values[input.id] = pin
                if (presetMeetingId) setDisabledInputs(prevArray => [...prevArray, input.id])
            }
            setFormValues(values);
        } 
    })

  }, [formInputs, presetMeetingId])

  useEffect(() => {
    onHasAllRequired(hasAllRequired)
  }, [hasAllRequired, onHasAllRequired])

  useEffect(() => {
    let completed = requiredInputs.every(input => formValues[input.id])
    setHasAllRequired(completed)
    
    inputs.forEach(input => {
        switch (input.resultAction) {
            case 'sipheader':
                let headers = inputSipHeaders 
                headers[input.resultSipHeader] = formValues[input.id]
                setInputSipHeaders(headers)
                onSipHeadersChange(headers)
                break;
            case 'extension':
                onExtensionChange(formValues[input.id])
                break;
            case 'callername':
                onCallerNameChange(formValues[input.id])
                break;
            case 'webhook':
                let params = webhookParams
                params[input.id] = formValues[input.id]
                setWebhookParams(params)
                onWebhookParamsChange(params)
                break;
            case 'meetingid':
                onMeetingIdChange(formValues[input.id])
                break;
            case 'pin':
                onPINChange(formValues[input.id])
                break;
            default:
                break;
        }
    })

  }, [formValues, inputs, requiredInputs])

  const isValidInput = (input) => {
    let error = null
    const validInputTypes = ['text', 'select', 'checkbox', 'rating', 'slider']

    const validPostCallInputActions = ['webhook']
    const validPreCallInputActions = ['sipheader', 'extension', 'callername', 'meetingid', 'pin', ...validPostCallInputActions]
    var validInputActions = null

    if (precall) {
        validInputActions = validPreCallInputActions
    } else {
        validInputActions = validPostCallInputActions
    }

    if (!input.hasOwnProperty('id')) error = 'NO_ID_KEY'
    if (!input.hasOwnProperty('type')) error = 'NO_TYPE_KEY'
    if (!input.hasOwnProperty('label')) error = 'NO_LABEL_KEY'
    if (!input.hasOwnProperty('required')) error = 'NO_REQUIRED_KEY'
    if (!input.hasOwnProperty('resultAction')) error = 'NO_RESULT_ACTION_KEY'
    if (!input.hasOwnProperty('defaultValue')) error = 'NO_DEFAULT_VALUE_KEY'
    if (!validInputTypes.includes(input.type)) error = 'INVALID_TYPE'
    if (!validInputActions.includes(input.resultAction)) error = 'INVALID_RESULT_ACTION'

    switch (input.type) {
        case 'select':
            if (!input.hasOwnProperty('selectItems')) error = 'INVALID_SELECT_ITEMS'
            if (!Array.isArray(input.selectItems)) error = 'INVALID_SELECT_ITEMS'
            if (
                input.selectItems.some(
                    item => !item.hasOwnProperty('label') || !item.hasOwnProperty('value')
                )
            ) error = "INVALID_SELECT_ITEMS"
            break;
        case 'rating':
            if (!input.hasOwnProperty('precision')) error = 'NO_RATING_PRECISION'
            if (!input.hasOwnProperty('size')) error = 'NO_RATING_SIZE'
            if (!input.hasOwnProperty('max')) error = 'NO_RATING_MAX'
            break;
        case 'slider':
            if (!input.hasOwnProperty('min')) error = 'NO_SLIDER_MIN'
            if (!input.hasOwnProperty('max')) error = 'NO_SLIDER_MAX'
            break;
        default:
            break;
    }

    switch (input.resultAction) {
        case 'sipheader':
            if (!input.hasOwnProperty('resultSipHeader')) error = 'NO_RESULT_SIP_HEADER_KEY'
            break;
        default:
            break;
    }

    if (error) {
        console.log(`Question ERROR [${error}]`, input)
        return false
    } else {
        return true
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: checked,
    });
  };

  const callWebhook = async () => {
    const response = await fetch(resultWebhook, {
        method: 'POST',
        body: JSON.stringify(webhookParams),
    });
  };

  const renderInput = (input, i) => {
    switch (input.type) {
        case 'text':
            input = 
                <FormControl key={i} fullWidth margin='normal'>
                    <TextField 
                        key={'textinput-' + i}
                        id={input.id}
                        name={input.id}
                        label={input.label}
                        defaultValue={formValues.hasOwnProperty(input.id) ? formValues[input.id] : ''}
                        onChange={handleInputChange}
                        disabled={disabledInputs.includes(input.id)}
                    >
                    </TextField>
                </FormControl>
            break;
        case 'select':
            input = <FormControl key={i} fullWidth margin='normal'>
                        <InputLabel id={'label-' + input.id}>{input.label}</InputLabel>
                        <Select
                            key={'selectinput-' + i}
                            id={input.id}
                            name={input.id}
                            labelId={'label-' + input.id}
                            label={input.label}
                            value={formValues.hasOwnProperty(input.id) ? formValues[input.id] : ''}
                            onChange={handleInputChange}
                        >
                            {input.selectItems.map((item, i) => {
                                return <MenuItem key={'item-' + i} value={item.value}>{item.label}</MenuItem>
                            })}
                        </Select> 
                    </FormControl>
            break;
        case 'checkbox':
            input = 
                <FormControl key={i} fullWidth margin='normal'>
                    <FormControlLabel
                        key={'checkbox-' + i}
                        label={input.label}
                        labelPlacement='start'
                        control={
                            <Checkbox 
                                id={input.id}
                                name={input.id}
                                checked={formValues.hasOwnProperty(input.id) ? formValues[input.id] : false}
                                onChange={handleCheckboxChange}
                            />
                        }
                    />
                </FormControl>
            break;
        case 'rating':
            input = 
                <FormControl key={i} fullWidth margin='normal'>
                    <Typography gutterBottom sx={{textAlign: 'left'}}>{input.label}</Typography>
                    <Rating 
                        key={'ratinginput-' + i}
                        id={input.id}
                        name={input.id}
                        label={input.label}
                        defaultValue={formValues.hasOwnProperty(input.id) ? formValues[input.id] : ''}
                        onChange={handleInputChange}
                        precision={input.precision}
                        size={input.size}
                        max={input.max}
                    />
                </FormControl>
            break;
        case 'slider':
            input = 
                <FormControl key={i} fullWidth margin='normal' sx={{textAlign: 'left'}}>
                    <Typography gutterBottom>{input.label}</Typography>
                    <Slider 
                        key={'sliderinput-' + i}
                        id={input.id}
                        name={input.id}
                        label={input.label}
                        defaultValue={formValues.hasOwnProperty(input.id) ? formValues[input.id] : ''}
                        onChange={handleInputChange}
                        valueLabelDisplay="auto"
                        color="primary"
                        min={input.min}
                        max={input.max}
                    />
                </FormControl>
            break;
        default:
            break;
    }
    return (input)
  }

  return (
    <div className='live-call-forminputs'>
            {inputs.map((input, i) => {
                return renderInput(input, i)
            })}
    </div>
  )
}
